import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSessionStore } from '../../stores/sessionStore';
import { apiFetch, getAuthHeaders } from '../../services/http';
import { toast } from 'sonner';
import type { IntegrationHeartbeatData } from './heartbeatTypes';

export function useIntegrationHeartbeat() {
  const token = useSessionStore(state => state.session?.access_token);
  const activeTenantId = useSessionStore(state => state.activeTenantId);
  const queryClient = useQueryClient();
  const headers = { ...getAuthHeaders(token), ...(activeTenantId ? { 'x-tenant-id': activeTenantId } : {}) };
  // Atualiza a conexão do tenant a cada minuto.
  const { data, isFetching, isError } = useQuery<IntegrationHeartbeatData>({
    queryKey: ['integration-heartbeat', token, activeTenantId],
    queryFn: () => apiFetch<IntegrationHeartbeatData>('/v1/app/integrations/heartbeat', { headers }),
    enabled: Boolean(token),
    refetchInterval: 60000,
    staleTime: 30000,
    retry: 1,
  });

  const checkMutation = useMutation({
    mutationFn: () => apiFetch<IntegrationHeartbeatData>('/v1/app/integrations/heartbeat/check', { method: 'POST', headers }),
    onMutate: () => ({ queryKey: ['integration-heartbeat', token, activeTenantId] }),
    onSuccess: (res, _variables, context) => {
      queryClient.setQueryData(context.queryKey, res);
      const op = res?.summary?.operational || 0;
      const tot = res?.summary?.total || 0;
      if (tot === 0) {
        toast.info('Nenhuma integração configurada para este tenant.');
      } else if (res?.healthy && op === tot) {
        toast.success(`Integrações verificadas: ${op}/${tot} conexões operacionais.`);
      } else if (op > 0) {
        toast.warning(`Integrações: ${op}/${tot} conexões operacionais. Verifique os alertas.`);
      } else {
        toast.error(`Falha nas integrações: ${res?.errors?.join(', ') || 'Verifique as credenciais'}`);
      }
    },
    onError: (err: Error) => {
      toast.error(`Falha ao testar conectividade das integrações: ${err.message}`);
    }
  });

  const summary = data?.summary || { total: 0, operational: 0, warning: 0, error: 0 };
  const channels = data?.channels || {};
  const channelList = Object.values(channels);
  const hasChannels = summary.total > 0;
  const isHealthy = !isError && data?.healthy !== false;
  const isChecking = isFetching || checkMutation.isPending;
  return { data, isFetching, checkMutation, summary, channelList, hasChannels, isHealthy, isChecking };
}
